import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Clock, ToggleLeft, ToggleRight, Copy, Bell, CalendarCheck, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { copyToClipboard } from "../../lib/clipboard";

const PLACEHOLDERS = "{cliente_nome} · {empresa_nome} · {revendedora_nome} · {valor} · {vencimento} · {link_pagamento} · {pix}";

function Toggle({ on, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex items-center gap-2 text-xs tracking-widest uppercase font-medium">
      {on ? <ToggleRight size={24} weight="fill" color="#3F6B45" /> : <ToggleLeft size={24} />}
      {on ? "Ativo" : "Inativo"}
    </button>
  );
}

export default function AdminBillingRuler() {
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);
  const [weights, setWeights] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try { setR((await api.get("/whatsapp/ruler")).data); } catch { toast.error("Erro ao carregar"); }
    try { setWeights((await api.get("/whatsapp/score-weights")).data); } catch { /* ignore */ }
  };
  useEffect(() => {
    (async () => {
      try { setR((await api.get("/whatsapp/ruler")).data); } catch { toast.error("Erro ao carregar"); }
      try { setWeights((await api.get("/whatsapp/score-weights")).data); } catch { /* ignore */ }
    })();
  }, []);

  const saveWeights = async () => {
    try { await api.put("/whatsapp/score-weights", weights); toast.success("Pontuacao salva"); }
    catch { toast.error("Erro ao salvar pontuacao"); }
  };

  const setStage = (stage, patch) => setR((p) => ({ ...p, [stage]: { ...p[stage], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const { reminder, pre_due, due, overdue, confirmed, inadimplencia, enabled, send_hour, quiet } = r;
      await api.put("/whatsapp/ruler", { enabled, reminder, pre_due, due, overdue, confirmed, inadimplencia, send_hour, quiet });
      toast.success("Regua de cobranca salva");
      load();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const cronCmd = r ? `0 9 * * * curl -s -X POST "${backend}/api/whatsapp/cron/run" -H "X-Cron-Token: ${r.cron_token}"` : "";
  const copyCron = async () => { (await copyToClipboard(cronCmd)) ? toast.success("Comando copiado") : toast.error("Falha ao copiar"); };

  if (!r) return <div className="text-sm tracking-widest uppercase text-[#5C5A57]" data-testid="ruler-loading">Carregando...</div>;

  return (
    <div data-testid="admin-ruler-page" className="max-w-3xl">
      <div className="topbar">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Cobranca automatica</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Regua de Cobranca</h1>
        </div>
        <Clock size={42} weight="duotone" />
      </div>

      <section className="border border-[#EBE5DF] bg-white p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="section-title">Disparo automatico</h2>
            <div className="text-xs text-[#5C5A57] mt-1">As cobrancas saem do <strong>numero oficial unico</strong> da empresa. A revendedora de cada cliente e identificada automaticamente. Use {"{placeholders}"} nas mensagens.</div>
          </div>
          <Toggle on={r.enabled} onClick={() => setR({ ...r, enabled: !r.enabled })} testid="ruler-enabled-toggle" />
        </div>
        <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mt-3 bg-[#F5F0EB] p-2">Placeholders: {PLACEHOLDERS}</div>
      </section>

      {/* Horario de envio / Nao perturbe */}
      <section className="border border-[#EBE5DF] bg-white p-6 mb-6" data-testid="ruler-window">
        <div className="flex items-center justify-between mb-3">
          <h3 className="section-title flex items-center gap-2"><Clock size={18} weight="duotone" /> Horario de envio (fuso Brasil)</h3>
          <Toggle on={r.quiet?.enabled !== false} onClick={() => setStage("quiet", { enabled: !(r.quiet?.enabled !== false) })} testid="quiet-toggle" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Horario padrao</label>
            <input type="time" value={r.send_hour || "09:00"} onChange={(e) => setR({ ...r, send_hour: e.target.value })} className="input-underline" data-testid="send-hour-input" />
          </div>
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Nao enviar antes das</label>
            <input type="time" value={r.quiet?.not_before || "08:00"} onChange={(e) => setStage("quiet", { not_before: e.target.value })} className="input-underline" data-testid="not-before-input" />
          </div>
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Nao enviar apos as</label>
            <input type="time" value={r.quiet?.not_after || "20:00"} onChange={(e) => setStage("quiet", { not_after: e.target.value })} className="input-underline" data-testid="not-after-input" />
          </div>
        </div>
        <div className="text-[11px] text-[#5C5A57] mt-3">Cobrancas geradas fora desse horario sao agendadas automaticamente para o proximo horario valido. O cron diario envia as agendadas que ja podem sair.</div>
      </section>

      {/* Lembrete */}
      <section className="border border-[#EBE5DF] bg-white p-6 mb-6" data-testid="ruler-reminder">
        <div className="flex items-center justify-between mb-3">
          <h3 className="section-title flex items-center gap-2"><Bell size={18} weight="duotone" /> Lembrete antes do vencimento</h3>
          <Toggle on={r.reminder.enabled} onClick={() => setStage("reminder", { enabled: !r.reminder.enabled })} testid="reminder-toggle" />
        </div>
        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Dias antes do vencimento</label>
        <input type="number" min="1" max="30" value={r.reminder.days_before} onChange={(e) => setStage("reminder", { days_before: Number(e.target.value) })} className="input-underline w-24 mb-3" data-testid="reminder-days-input" />
        <div className="text-[11px] text-[#8A6D1F] bg-[#FBF6E7] p-2 mb-3">Este estagio envia <strong>apenas o lembrete</strong> (sem PIX). O link de pagamento e gerado no dia anterior ao vencimento (D-1).</div>
        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Mensagem (vazio = padrao)</label>
        <textarea rows={3} value={r.reminder.template} onChange={(e) => setStage("reminder", { template: e.target.value })} className="w-full p-3 border border-[#EBE5DF] text-sm" placeholder="Ola {cliente_nome}. Lembramos que sua parcela no valor de {valor} vence em {vencimento}. No dia anterior enviaremos o link para pagamento." data-testid="reminder-template" />
      </section>

      {/* PIX antes do vencimento (D-1) */}
      <section className="border border-[#EBE5DF] bg-white p-6 mb-6" data-testid="ruler-predue">
        <div className="flex items-center justify-between mb-3">
          <h3 className="section-title flex items-center gap-2"><Bell size={18} weight="duotone" color="#2D6CB4" /> Gerar PIX antes do vencimento</h3>
          <Toggle on={r.pre_due?.enabled !== false} onClick={() => setStage("pre_due", { enabled: !(r.pre_due?.enabled !== false) })} testid="predue-toggle" />
        </div>
        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Dias antes do vencimento (D-1)</label>
        <input type="number" min="1" max="10" value={r.pre_due?.days_before ?? 1} onChange={(e) => setStage("pre_due", { days_before: Number(e.target.value) })} className="input-underline w-24 mb-3" data-testid="predue-days-input" />
        <div className="text-[11px] text-[#2D6CB4] bg-[#EEF4FB] p-2 mb-3">Gera a cobranca PIX (QR Code + copia e cola + link) com validade ate <strong>o dia seguinte ao vencimento, 23:59</strong>. No dia do vencimento o mesmo PIX e reenviado (sem duplicar).</div>
        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Mensagem (vazio = padrao)</label>
        <textarea rows={3} value={r.pre_due?.template || ""} onChange={(e) => setStage("pre_due", { template: e.target.value })} className="w-full p-3 border border-[#EBE5DF] text-sm" placeholder="Sua parcela de {valor} vence amanha ({vencimento}). PIX copia e cola: {pix}" data-testid="predue-template" />
      </section>

      {/* No vencimento */}
      <section className="border border-[#EBE5DF] bg-white p-6 mb-6" data-testid="ruler-due">
        <div className="flex items-center justify-between mb-3">
          <h3 className="section-title flex items-center gap-2"><CalendarCheck size={18} weight="duotone" /> No dia do vencimento</h3>
          <Toggle on={r.due.enabled} onClick={() => setStage("due", { enabled: !r.due.enabled })} testid="due-toggle" />
        </div>
        <div className="text-[11px] text-[#3F6B45] bg-[#EFF5EF] p-2 mb-3">Reenvia o <strong>mesmo PIX</strong> gerado no dia anterior (nao cria cobranca nova enquanto a atual estiver valida).</div>
        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Mensagem (vazio = padrao)</label>
        <textarea rows={3} value={r.due.template} onChange={(e) => setStage("due", { template: e.target.value })} className="w-full p-3 border border-[#EBE5DF] text-sm" data-testid="due-template" />
      </section>

      {/* Apos o vencimento */}
      <section className="border border-[#EBE5DF] bg-white p-6 mb-6" data-testid="ruler-overdue">
        <div className="flex items-center justify-between mb-3">
          <h3 className="section-title flex items-center gap-2"><WarningCircle size={18} weight="duotone" /> Apos o vencimento</h3>
          <Toggle on={r.overdue.enabled} onClick={() => setStage("overdue", { enabled: !r.overdue.enabled })} testid="overdue-toggle" />
        </div>
        <div className="flex flex-wrap gap-6 mb-3">
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Dias de cobranca apos vencimento</label>
            <input type="text" value={(r.overdue.days || [1, 3, 7]).join(", ")} onChange={(e) => setStage("overdue", { days: e.target.value.split(",").map((x) => parseInt(x.trim(), 10)).filter((x) => x > 0) })} className="input-underline w-40" placeholder="1, 3, 7" data-testid="overdue-days-input" />
            <div className="text-[10px] text-[#5C5A57] mt-1">Ex.: 1, 3, 7 (D+1, D+3, D+7). Gera novo PIX com multa e juros.</div>
          </div>
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Aplicar inadimplencia em (D+)</label>
            <input type="number" min="1" max="60" value={r.inadimplencia?.day ?? 7} onChange={(e) => setStage("inadimplencia", { day: Number(e.target.value) })} className="input-underline w-24" data-testid="inadimplencia-day-input" />
            <div className="text-[10px] text-[#5C5A57] mt-1">Reduz score, bloqueia novo limite e notifica a revendedora.</div>
          </div>
        </div>
        <div className="text-[11px] text-[#8B5E5E] bg-[#FBEDED] p-2 mb-3">Multa fixa de <strong>2%</strong> + juros de mora de <strong>1% ao mes</strong> (proporcional por dia). O valor principal da parcela nao e alterado; o detalhamento fica salvo na cobranca.</div>
        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Mensagem (vazio = padrao)</label>
        <textarea rows={3} value={r.overdue.template} onChange={(e) => setStage("overdue", { template: e.target.value })} className="w-full p-3 border border-[#EBE5DF] text-sm" data-testid="overdue-template" />
      </section>

      {/* Pagamento confirmado */}
      <section className="border border-[#EBE5DF] bg-white p-6 mb-6" data-testid="ruler-confirmed">
        <div className="flex items-center justify-between mb-3">
          <h3 className="section-title flex items-center gap-2"><CheckCircle size={18} weight="duotone" color="#3F6B45" /> Pagamento confirmado</h3>
          <Toggle on={r.confirmed?.enabled !== false} onClick={() => setStage("confirmed", { enabled: !(r.confirmed?.enabled !== false) })} testid="confirmed-toggle" />
        </div>
        <div className="text-[11px] text-[#5C5A57] mb-3">Enviada automaticamente pelo numero oficial assim que uma parcela e quitada (PIX ou baixa de comprovante).</div>
        <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Mensagem (vazio = padrao)</label>
        <textarea rows={3} value={r.confirmed?.template || ""} onChange={(e) => setStage("confirmed", { template: e.target.value })} className="w-full p-3 border border-[#EBE5DF] text-sm" placeholder="Ola {cliente_nome}! Aqui e o atendimento oficial da {empresa_nome}. Confirmamos o pagamento da sua parcela de {valor}. Obrigado!" data-testid="confirmed-template" />
      </section>

      <button onClick={save} disabled={saving} className="brand" data-testid="ruler-save-btn">{saving ? "Salvando..." : "Salvar regua"}</button>

      {/* Pontuacao por classificacao (Score) */}
      {weights && (
        <section className="border border-[#EBE5DF] bg-white p-6 mt-8" data-testid="score-weights">
          <h3 className="section-title mb-1">Pontuacao do Score por classificacao</h3>
          <p className="text-xs text-[#5C5A57] mb-4">A IA classifica as respostas das clientes e ajusta o score automaticamente. Numero invalido marca o cadastro para revisao.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[["vai_pagar", "Vai pagar", "#3F6B45"], ["enviou_comprovante", "Enviou comprovante", "#2D6CB4"], ["solicitou_prazo", "Solicitou prazo", "#C9A227"], ["sem_resposta", "Sem resposta", "#D97A34"], ["recusa", "Recusa pagamento", "#B4564F"]].map(([k, label, color]) => (
              <div key={k} className="flex items-center justify-between border border-[#F0EBE5] px-3 py-2">
                <span className="flex items-center gap-2 text-sm"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} /> {label}</span>
                <input type="number" value={weights[k]} onChange={(e) => setWeights({ ...weights, [k]: Number(e.target.value) })} className="input-underline w-20 text-right" data-testid={`weight-${k}`} />
              </div>
            ))}
            <div className="flex items-center justify-between border border-[#F0EBE5] px-3 py-2 bg-[#FAF7F3]">
              <span className="flex items-center gap-2 text-sm"><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#2A2A2A]" /> Numero invalido</span>
              <span className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Marca p/ revisao</span>
            </div>
          </div>
          <button onClick={saveWeights} className="brand mt-4" data-testid="save-weights-btn">Salvar pontuacao</button>
        </section>
      )}

      {/* Cron */}
      <section className="border border-[#EBE5DF] bg-[#0A0A0A] text-white p-6 mt-8" data-testid="ruler-cron">
        <h3 className="section-title text-white">Cron diario (VPS)</h3>
        <p className="text-xs text-[#B5A89E] mt-1 mb-3">Adicione no crontab da VPS para o disparo automatico rodar todo dia as 09h. Mantenha o token em segredo.</p>
        <code className="block bg-black/40 p-3 text-[11px] font-mono break-all" data-testid="cron-command">{cronCmd}</code>
        <button onClick={copyCron} className="brand mt-3" data-testid="copy-cron-btn"><Copy size={14} className="inline mr-2" />Copiar comando</button>
      </section>
    </div>
  );
}
